import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import { apiFetch, setUnauthorizedHandler } from "../lib/api";

const STORAGE_KEY = "stocks_auth";

const AuthContext = createContext(null);

function readStored() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed?.token ? parsed : null;
  } catch {
    return null;
  }
}

function AuthProvider({ children }) {
  // Hydrated synchronously so a logged-in user never sees the login gate flash.
  const [auth, setAuth] = useState(readStored);
  const [booting, setBooting] = useState(Boolean(auth));

  const logout = useCallback(() => {
    localStorage.removeItem(STORAGE_KEY);
    setAuth(null);
    setBooting(false);
  }, []);

  const login = useCallback((token, user) => {
    const next = { token, user };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    setAuth(next);
    setBooting(false);
  }, []);

  // Any 401 from anywhere in the app drops the stored session.
  useEffect(() => {
    setUnauthorizedHandler(logout);
    return () => setUnauthorizedHandler(null);
  }, [logout]);

  // Validate a stored token once. Under StrictMode this runs twice in dev,
  // which is harmless for an idempotent GET.
  useEffect(() => {
    if (!auth?.token) return;
    let cancelled = false;

    apiFetch("/api/auth/me", { token: auth.token })
      .then((data) => {
        if (!cancelled && data?.user) {
          setAuth((prev) => (prev ? { ...prev, user: data.user } : prev));
        }
      })
      .catch(() => {
        // 401 already triggered logout via the handler above; anything else
        // (server down) shouldn't log the user out.
      })
      .finally(() => {
        if (!cancelled) setBooting(false);
      });

    return () => {
      cancelled = true;
    };
    // Keyed on the token only. Depending on the whole auth object would loop,
    // since the .then() above writes back into it.
  }, [auth?.token]);

  const value = {
    token: auth?.token || null,
    user: auth?.user || null,
    isAuthed: Boolean(auth?.token),
    booting,
    login,
    logout,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
}

export { AuthProvider, useAuth };
